import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';

interface StrategicPlan {
  id: string;
  title: string;
  description?: string;
  plan_type: string;
  status: string;
  owner_name?: string;
  start_date?: string;
  end_date?: string;
  goals: Array<{
    id: string;
    title: string;
    status: string;
    current_value: number;
    target_value?: number;
  }>;
  key_results: Array<{
    id: string;
    goal_id: string;
    title: string;
    current_value: number;
    target_value: number;
    status: string;
  }>;
  progress_percentage: number;
  tags: string[];
  created_at: string;
  updated_at: string;
}

const STATUS_COLORS: Record<string, string> = {
  draft: 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400',
  active: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400',
  on_track: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
  at_risk: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400',
  off_track: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
  completed: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400',
  archived: 'bg-gray-100 text-gray-600 dark:bg-gray-900/30 dark:text-gray-500',
};

const PLAN_TYPE_LABELS: Record<string, string> = {
  annual: 'Annual Plan',
  quarterly: 'Quarterly Plan',
  okr: 'OKR',
  initiative: 'Initiative',
};

export function Strategy() {
  const queryClient = useQueryClient();
  const [showCreate, setShowCreate] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState<StrategicPlan | null>(null);
  const [filterType, setFilterType] = useState('');
  const [filterStatus, setFilterStatus] = useState('');

  // Form state
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [planType, setPlanType] = useState('quarterly');

  const queryParams = new URLSearchParams();
  if (filterType) queryParams.set('plan_type', filterType);
  if (filterStatus) queryParams.set('status', filterStatus);

  const { data, isLoading } = useQuery({
    queryKey: ['strategy-plans', filterType, filterStatus],
    queryFn: () => api<{ data: StrategicPlan[] }>(`strategy/plans?${queryParams.toString()}`),
  });

  const createMutation = useMutation({
    mutationFn: (body: { title: string; description: string; plan_type: string }) =>
      api('strategy/plans', { body }),
    onSuccess: () => {
      setShowCreate(false);
      setTitle('');
      setDescription('');
      setPlanType('quarterly');
      queryClient.invalidateQueries({ queryKey: ['strategy-plans'] });
    },
  });

  const handleCreate = () => {
    if (!title.trim()) return;
    createMutation.mutate({ title: title.trim(), description: description.trim(), plan_type: planType });
  };

  const plans = data?.data ?? [];

  return (
    <div className="p-8 max-w-6xl mx-auto">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold text-foreground mb-1">Strategy</h1>
          <p className="text-muted-foreground">Strategic plans, OKRs, and initiative tracking.</p>
        </div>
        <button
          onClick={() => setShowCreate(!showCreate)}
          className="bg-primary text-primary-foreground hover:bg-primary/90 px-4 py-2 rounded-md text-sm font-medium"
        >
          Create Plan
        </button>
      </div>

      {/* Create form */}
      {showCreate && (
        <div className="mb-6 rounded-lg border bg-card p-6">
          <h3 className="text-sm font-medium mb-4">Create a new plan</h3>
          <div className="grid grid-cols-3 gap-4 mb-4">
            <div className="col-span-2">
              <label className="block text-sm text-muted-foreground mb-1">Title</label>
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Q2 2026 Growth Strategy"
                className="w-full border rounded-md px-3 py-2 bg-background text-sm"
              />
            </div>
            <div>
              <label className="block text-sm text-muted-foreground mb-1">Type</label>
              <select
                value={planType}
                onChange={(e) => setPlanType(e.target.value)}
                className="w-full border rounded-md px-3 py-2 bg-background text-sm"
              >
                <option value="annual">Annual Plan</option>
                <option value="quarterly">Quarterly Plan</option>
                <option value="okr">OKR</option>
                <option value="initiative">Initiative</option>
              </select>
            </div>
          </div>
          <div className="mb-4">
            <label className="block text-sm text-muted-foreground mb-1">Description</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="What is the goal of this plan?"
              rows={2}
              className="w-full border rounded-md px-3 py-2 bg-background text-sm resize-none"
            />
          </div>
          <div className="flex gap-2">
            <button
              onClick={handleCreate}
              disabled={!title.trim() || createMutation.isPending}
              className="bg-primary text-primary-foreground hover:bg-primary/90 px-4 py-2 rounded-md text-sm font-medium disabled:opacity-50"
            >
              {createMutation.isPending ? 'Creating...' : 'Create Plan'}
            </button>
            <button
              onClick={() => setShowCreate(false)}
              className="px-4 py-2 rounded-md text-sm border hover:bg-muted"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="flex gap-4 mb-6">
        <select
          value={filterType}
          onChange={(e) => setFilterType(e.target.value)}
          className="border rounded-md px-3 py-2 bg-background text-sm min-w-[150px]"
        >
          <option value="">All Types</option>
          <option value="annual">Annual</option>
          <option value="quarterly">Quarterly</option>
          <option value="okr">OKR</option>
          <option value="initiative">Initiative</option>
        </select>
        <select
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
          className="border rounded-md px-3 py-2 bg-background text-sm min-w-[150px]"
        >
          <option value="">All Statuses</option>
          <option value="draft">Draft</option>
          <option value="active">Active</option>
          <option value="on_track">On Track</option>
          <option value="at_risk">At Risk</option>
          <option value="completed">Completed</option>
        </select>
      </div>

      {/* Plans list */}
      {isLoading ? (
        <div className="space-y-3">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-24 bg-muted animate-pulse rounded-lg" />
          ))}
        </div>
      ) : plans.length === 0 ? (
        <div className="rounded-lg border bg-card p-12 text-center text-muted-foreground">
          No strategic plans yet. Create one to start tracking your goals.
        </div>
      ) : (
        <div className="space-y-3">
          {plans.map((plan) => (
            <button
              key={plan.id}
              onClick={() => setSelectedPlan(selectedPlan?.id === plan.id ? null : plan)}
              className={`w-full text-left rounded-lg border p-5 transition-colors ${
                selectedPlan?.id === plan.id ? 'border-primary bg-primary/5' : 'bg-card hover:border-primary/30'
              }`}
            >
              <div className="flex items-start justify-between mb-3">
                <div>
                  <h3 className="font-medium text-foreground">{plan.title}</h3>
                  {plan.description && (
                    <p className="text-sm text-muted-foreground mt-1 line-clamp-1">{plan.description}</p>
                  )}
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <span className="text-xs px-2 py-0.5 rounded-full bg-muted text-muted-foreground">
                    {PLAN_TYPE_LABELS[plan.plan_type] ?? plan.plan_type}
                  </span>
                  <span className={`text-xs px-2 py-0.5 rounded-full ${STATUS_COLORS[plan.status] ?? STATUS_COLORS['draft']}`}>
                    {plan.status.replace('_', ' ')}
                  </span>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <div className="flex-1">
                  <div className="flex justify-between text-xs text-muted-foreground mb-1">
                    <span>Progress</span>
                    <span>{plan.progress_percentage}%</span>
                  </div>
                  <div className="w-full bg-muted rounded-full h-2">
                    <div
                      className="bg-primary h-2 rounded-full transition-all"
                      style={{ width: `${plan.progress_percentage}%` }}
                    />
                  </div>
                </div>
                <div className="text-xs text-muted-foreground shrink-0">
                  {plan.goals.length} goals / {plan.key_results.length} KRs
                </div>
                {plan.owner_name && (
                  <span className="text-xs text-muted-foreground shrink-0">{plan.owner_name}</span>
                )}
              </div>

              {/* Expanded goals */}
              {selectedPlan?.id === plan.id && plan.goals.length > 0 && (
                <div className="mt-4 pt-4 border-t space-y-2">
                  <h4 className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Goals</h4>
                  {plan.goals.map((goal) => (
                    <div key={goal.id} className="flex items-center justify-between text-sm">
                      <span className="text-foreground">{goal.title}</span>
                      <span className={`text-xs px-2 py-0.5 rounded-full ${STATUS_COLORS[goal.status] ?? STATUS_COLORS['draft']}`}>
                        {goal.status.replace('_', ' ')}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
